import { PACIFIC_TIME_ZONE } from '@orn/shared-web/utils/datetime/formatInstantInTimeZone';

import {
  AdminSortDirection,
  AdminWorkItemAssigneeFilter,
  AdminWorkItemSortField,
  WorkItemUserCategory,
} from '../../__generated__/globalTypes';

export const WORK_ITEM_CATEGORY_LABEL: Record<WorkItemUserCategory, string> = {
  [WorkItemUserCategory.Update]: 'Update',
  [WorkItemUserCategory.Reminder]: 'Reminder',
  [WorkItemUserCategory.Config]: 'Config',
};

export const WORK_ITEM_ASSIGNEE_LABEL: Record<AdminWorkItemAssigneeFilter, string> = {
  [AdminWorkItemAssigneeFilter.Assistant]: 'Assistant',
  [AdminWorkItemAssigneeFilter.Household]: 'Household',
};

/** Which date column orders the table, and which way. */
export interface IWorkItemSort {
  field: AdminWorkItemSortField;
  direction: AdminSortDirection;
}

/** Most recently touched first — the row an operator is usually looking for. */
export const DEFAULT_WORK_ITEM_SORT: IWorkItemSort = {
  field: AdminWorkItemSortField.UpdatedAt,
  direction: AdminSortDirection.Desc,
};

/**
 * The sort a header click yields: clicking the active column flips its
 * direction; clicking another column takes it over, newest first.
 */
export function toggleWorkItemSort(current: IWorkItemSort, field: AdminWorkItemSortField): IWorkItemSort {
  if (current.field !== field) {
    return { field, direction: AdminSortDirection.Desc };
  }
  return {
    field,
    direction: current.direction === AdminSortDirection.Desc ? AdminSortDirection.Asc : AdminSortDirection.Desc,
  };
}

/** Everything except the page cursor that decides which rows match, and in what order. */
export interface IWorkItemFilterState {
  familyId: string | null;
  assignee: AdminWorkItemAssigneeFilter | null;
  dueNow: boolean;
  hideArchived: boolean;
  /** The schedule whose emissions the table is narrowed to, or null for every row. */
  scheduleId: string | null;
  sort: IWorkItemSort;
}

/**
 * Identity of a filter set, ignoring the page cursor.
 *
 * Lets a caller tell "the operator turned a page" from "the operator changed
 * what they are asking for" — the two look identical to Apollo, which empties
 * `data` for any variable set it has not cached. Holding the previous page over
 * a *cursor* move keeps the table from flickering; holding it over a *filter*
 * change would render stale rows as though they matched the new filter. The
 * sort counts as a filter for the same reason: a cursor minted under one order
 * lands mid-window under another.
 */
export function workItemFilterSignature(filters: IWorkItemFilterState): string {
  return [
    filters.familyId ?? '',
    filters.assignee ?? 'anyone',
    filters.dueNow ? 'due' : 'all',
    filters.hideArchived ? 'live' : 'archived',
    filters.scheduleId ?? '',
    `${filters.sort.field}:${filters.sort.direction}`,
  ].join('|');
}

/** First segment of a uuid — enough to eyeball and to match against a full id. */
export function abbreviateId(id: string): string {
  return id.slice(0, 8);
}

/** Cell preview budget for the prose columns — the full text lives in the modal. */
export const CELL_PREVIEW_MAX_CHARS = 250;

/** A prose cell's preview: at most `maxChars` characters, ellipsized. */
export function truncateForCell(text: string, maxChars: number): string {
  if (text.length <= maxChars) {
    return text;
  }
  return `${text.slice(0, maxChars)}…`;
}

/** The row's link columns, as the Subject cell reads them. */
export interface IWorkItemSubjectSource {
  transactionIds: string[];
  billIds: string[];
  merchantIds: string[];
  financialAccountId: string | null;
  bankConnectionId: string | null;
  recurringTransactionId: string | null;
  billSubmissionId: string | null;
}

/** One attached foreign key, labeled for the Subject cell. */
export interface IWorkItemSubjectRef {
  label: string;
  id: string;
}

/** One kind of attached foreign key with how many the row names — the cell's line. */
export interface IWorkItemSubjectCount {
  label: string;
  count: number;
}

/**
 * Per-kind tallies of {@link workItemSubjectRefs}, in the same order — the
 * Subject cell shows `txn: 2`; the full ids live in the modal.
 */
export function workItemSubjectCounts(row: IWorkItemSubjectSource): IWorkItemSubjectCount[] {
  const counts = new Map<string, number>();
  for (const ref of workItemSubjectRefs(row)) {
    counts.set(ref.label, (counts.get(ref.label) ?? 0) + 1);
  }
  return [...counts.entries()].map(([label, count]) => ({ label, count }));
}

/**
 * Every foreign key the row names, flattened into labeled refs — the array
 * columns contribute one ref per element, the scalar columns one when set.
 * Order groups by kind so a row naming several transactions reads as a run.
 */
export function workItemSubjectRefs(row: IWorkItemSubjectSource): IWorkItemSubjectRef[] {
  const refs: IWorkItemSubjectRef[] = [];
  for (const id of row.transactionIds) {
    refs.push({ label: 'txn', id });
  }
  for (const id of row.billIds) {
    refs.push({ label: 'bill', id });
  }
  for (const id of row.merchantIds) {
    refs.push({ label: 'merchant', id });
  }
  if (row.financialAccountId !== null) {
    refs.push({ label: 'account', id: row.financialAccountId });
  }
  if (row.bankConnectionId !== null) {
    refs.push({ label: 'bank conn', id: row.bankConnectionId });
  }
  if (row.recurringTransactionId !== null) {
    refs.push({ label: 'recurring', id: row.recurringTransactionId });
  }
  if (row.billSubmissionId !== null) {
    refs.push({ label: 'draft', id: row.billSubmissionId });
  }
  return refs;
}

/**
 * `MM/DD HH:mm` in Pacific. Deliberately not `formatTimeAgo` (the house
 * default): Created, Updated, and Due date are read against each other to
 * expose lag, and "3 days ago" vs "4 days ago" cannot show that. The year is
 * dropped as noise — the full timestamp lives in the cell's `title`.
 */
export function formatWorkItemTimestamp(iso: string): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: PACIFIC_TIME_ZONE,
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
    .format(new Date(iso))
    .replace(',', '');
}

/** Full Pacific timestamp for the `title` tooltip on a compact date cell. */
export function formatWorkItemTimestampFull(iso: string): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: PACIFIC_TIME_ZONE,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
  }).format(new Date(iso));
}
